import { QueryClient } from "@tanstack/react-query";
import { DataProvider } from "react-admin";
import { HttpError } from "react-admin";
import axios, { AxiosRequestConfig } from "axios";
import * as tokens from "api/auth/tokens";
import { config as mainConfig } from "config";
import { i18nProvider } from "i18n";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true, //TODO
      retry: 2,
    },
    //mutations: {
    //retryDelay: 10000, //TODO
    //},
  },
});

// Функция для получения текущей локали используя официальный i18nProvider
const getCurrentLocale = (): string => {
  // Используем официальный метод i18nProvider для получения текущей локали
  return i18nProvider.getLocale() || "en";
};

export const fetchWithToken = (config: AxiosRequestConfig): Promise<any> => {
  config.headers = {
    ...config.headers,
    ...tokens.headerToken(tokens.lastAccessToken()),
  };

  // Автоматически добавляем заголовок с текущей локалью из store
  const currentLocale = getCurrentLocale();
  if (currentLocale) {
    config.headers["X-Locale"] = currentLocale;
  }

  return axios(config)
    .then((response) => Promise.resolve(response))
    .catch((error) => {
      if (error.response && error.response.status === 401) {
        return tokens.tokenRefresher
          .refreshToken()
          .then((token) => {
            config.headers = {
              ...config.headers,
              ...tokens.headerToken(token),
            };

            // Добавляем заголовок с локалью и в повторный запрос
            // Получаем свежее значение локали на случай, если оно изменилось
            if (currentLocale) {
              config.headers["X-Locale"] = currentLocale;
            }
            return axios(config);
          })
          .then((response) => Promise.resolve(response))
          .catch((error) => {
            tokens.deleteAccessToken();
            return Promise.reject(error);
          });
      } else if (
        error.response &&
        (error.response.status < 200 || error.response.status >= 300)
      ) {
        const e = new HttpError(
          error.response.status,
          error.response.status,
          error.response.data,
        );
        return Promise.reject(e);
      }
      return Promise.reject(error);
    });
};

const dataProvider: DataProvider = {
  supportAbortSignal: true,
  getList: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}`,
      method: "get",
      params: {
        ...params.pagination,
        ...params.sort,
        filter: JSON.stringify(params.filter),
      },
    };

    return queryClient.fetchQuery({
      queryKey: [resource, params],
      queryFn: () =>
        fetchWithToken(config).then((response) => ({
          data: response.data,
          total: parseInt(response.headers["x-total-count"], 10),
        })),
    });
  },

  getOne: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}/${params.id}`,
      method: "get",
    };

    return queryClient.fetchQuery({
      queryKey: [resource, params.id],
      queryFn: () => fetchWithToken(config).then((response) => response.data),
    });
  },

  getMany: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}`,
      method: "get",
      params: { ids: params.ids },
    };

    return queryClient.fetchQuery({
      queryKey: [resource, "many", params.ids],
      queryFn: () =>
        fetchWithToken(config).then((response) => ({
          data: response.data,
        })),
    });
  },

  create: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}`,
      method: "post",
      data: params,
    };

    return fetchWithToken(config).then((response) => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      return response.data;
    });
  },

  update: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}/${params.id}`,
      method: "patch",
      data: params,
    };

    return fetchWithToken(config).then((response) => {
      //queryClient.invalidateQueries({queryKey: [resource, params.id]})
      queryClient.invalidateQueries({ queryKey: [resource] });
      return response.data;
    });
  },

  delete: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}/${params.id}`,
      method: "delete",
    };

    return fetchWithToken(config).then((response) => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      return response.data;
    });
  },

  deleteMany: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}`,
      method: "delete",
      data: { ids: params.ids },
    };

    return fetchWithToken(config).then((response) => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      return response.data;
    });
  },

  updateMany: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}`,
      method: "put",
      data: { ids: params.ids, data: params },
    };

    return fetchWithToken(config).then((response) => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      return response.data;
    });
  },

  getManyReference: (resource, params) => {
    const config: AxiosRequestConfig = {
      url: `${mainConfig.baseApiUrl()}/${resource}`,
      method: "get",
      params: {
        [params.target]: params.id,
        ...params.pagination,
        ...params.sort,
        filter: JSON.stringify(params.filter),
      },
    };

    return queryClient.fetchQuery({
      queryKey: [resource, "reference", params.target, params.id],
      queryFn: () =>
        fetchWithToken(config).then((response) => ({
          data: response.data,
          total: parseInt(response.headers["x-total-count"], 10),
        })),
    });
  },
};

export default dataProvider;
